import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Search, Star, ChevronRight, User, Users, Loader, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

const ProfessorsPage = () => {
  const { user } = useContext(AuthContext);
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'rating', 'popularity'
  const [ratingsEnabled, setRatingsEnabled] = useState(true);

  useEffect(() => {
    const fetchProfessors = async () => {
      try {
        setLoading(true);
        
        // Get school ID from the auth context instead of localStorage
        const schoolId = user?.schoolId || user?.school?.id;
        
        if (!schoolId) {
          throw new Error('School information not found');
        }
        
        console.log('Fetching professors for school:', schoolId);
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/professors?sort=${sortBy}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch professors');
        }
        
        const data = await response.json();
        console.log(`Fetched ${data.length} professors`);
        setProfessors(data);
        
        // Check if ratings are enabled by looking at the professor data
        // If ratings are disabled, avgRating will be null for all professors
        const hasRatingData = data.some(prof => prof.avgRating !== null);
        const ratingsAreEnabled = hasRatingData || data.some(prof => prof.ratingCount > 0);
        setRatingsEnabled(ratingsAreEnabled);
        
        // Reset sort to 'name' if ratings are disabled and current sort is rating-based
        if (!ratingsAreEnabled && (sortBy === 'rating' || sortBy === 'popularity')) {
          setSortBy('name');
        }
      } catch (err) {
        console.error('Error fetching professors:', err);
        setError(err.message);
        toast.error('Failed to load professors');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if user is logged in and has a school
    if (user) {
      fetchProfessors();
    } else {
      setLoading(false);
    }
  }, [user, sortBy]);

  // Filter professors based on search query
  const filteredProfessors = professors.filter(
    professor => professor.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderStarRating = (rating) => {
    if (!rating) return 'No ratings yet';
    
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-4 w-4 ${
              i < fullStars 
                ? 'text-yellow-400 fill-yellow-400' 
                : i === fullStars && hasHalfStar
                ? 'text-yellow-400 fill-yellow-400 half-star'
                : 'text-gray-300 dark:text-gray-600'
            }`} 
          />
        ))}
        <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
          <span className="text-gray-600 dark:text-gray-400">Loading professors...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Professors</h1>
        <div className="flex items-center">
          <Users className="h-5 w-5 mr-2 text-gray-500" />
          <span className="text-gray-600 dark:text-gray-400">{professors.length} Professors</span>
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className="flex flex-col md:flex-row md:items-center mb-6 space-y-4 md:space-y-0 md:space-x-4">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
            placeholder="Search professors by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex items-center">
          <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
            Sort by:
          </label>
          <select
            id="sortBy"
            name="sortBy"
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Name</option>
            {ratingsEnabled && <option value="rating">Rating</option>}
            {ratingsEnabled && <option value="popularity">Popularity</option>}
          </select>
        </div>
      </div>

      {/* Admin Actions - Add Professor Button for admins and moderators */}
      {user && (user.role === 'admin' || user.role === 'moderator') && (
        <div className="mb-6">
          <Link
            to="/professors/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <User className="mr-2 -ml-1 h-5 w-5" />
            Add Professor
          </Link>
        </div>
      )}

      {/* Professor List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {filteredProfessors.length > 0 ? (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredProfessors.map((professor) => (
              <li key={professor.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                <Link to={`/professors/${professor.id}`} className="block">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-gray-200 dark:bg-gray-700 p-2 rounded-full">
                          <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {professor.name}
                          </div>
                          <div className="mt-1">
                            {renderStarRating(professor.avgRating)}
                          </div>
                          {professor.courseSections && professor.courseSections.length > 0 && (
                            <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
                              <BookOpen size={12} className="mr-1" />
                              <span>
                                Teaching: {professor.courseSections.slice(0, 2).map(section => 
                                  section.courseCode
                                ).join(', ')}
                                {professor.courseSections.length > 2 && ` +${professor.courseSections.length - 2} more`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300">
                          {professor.ratingCount || 0} {professor.ratingCount === 1 ? 'review' : 'reviews'}
                        </span>
                        <ChevronRight className="h-5 w-5 text-gray-400 ml-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-4 py-5 sm:px-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No professors match your search criteria.' : 'No professors available.'}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-2 text-red-600 dark:text-red-400 hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessorsPage;